const FORBIDDEN_KEYS = new Set([
  'authorization',
  'cookie',
  'session',
  'session_id',
  'password',
  'secret',
  'account_id',
  'accountid',
  'user_id',
  'userid',
  'email',
  'prompt',
  'question',
  'answer',
  'response',
  'document',
  'shared_text',
  'sharedtext',
  'content',
  'filename',
  'file_name',
  'uploadid',
  'upload_id',
  'ai_input',
  'ai_output',
  'ai_output_choices',
  'metadata',
  'knowledge_laws',
  'context_agents',
  'error',
  'card',
  'payment_method',
]);
const SECRET_QUERY_KEY =
  /(code|token|secret|password|session|auth|key|email|account(?:_?id)?|user(?:_?id)?)/i;
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const MAX_PROPERTIES = 40;
const MAX_DEPTH = 3;
const MAX_STRING_LENGTH = 500;

export function sanitizeBrowserAnalyticsProperties(
  properties: Record<string, unknown>,
  pseudonymous: boolean,
): Record<string, unknown> {
  return sanitizeObject(properties, pseudonymous, 0);
}

function sanitizeObject(
  properties: Record<string, unknown>,
  pseudonymous: boolean,
  depth: number,
): Record<string, unknown> {
  if (depth >= MAX_DEPTH) return {};
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !isForbiddenKey(key))
      .slice(0, MAX_PROPERTIES)
      .map(([key, value]) => [
        key.slice(0, 80),
        sanitizeValue(key, value, pseudonymous, depth + 1),
      ])
      .filter(([, value]) => value !== undefined),
  );
}

function sanitizeValue(
  key: string,
  value: unknown,
  pseudonymous: boolean,
  depth: number,
): unknown {
  if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
    return value;
  }
  if (typeof value === 'string') {
    const normalizedKey = normalizeKey(key);
    const sanitized = normalizedKey.includes('url') || normalizedKey === 'referrer'
      ? sanitizeUrl(value)
      : value.trim().slice(0, MAX_STRING_LENGTH);
    return pseudonymous ? sanitized.replace(EMAIL, '[redacted-email]') : sanitized;
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, 20)
      .map((item) => sanitizeValue(key, item, pseudonymous, depth))
      .filter((item) => item !== undefined);
  }
  if (isPlainObject(value)) {
    return sanitizeObject(value, pseudonymous, depth);
  }
  return undefined;
}

function sanitizeUrl(value: string): string {
  try {
    const url = new URL(value);
    for (const key of [...url.searchParams.keys()]) {
      if (SECRET_QUERY_KEY.test(key)) url.searchParams.set(key, '[redacted]');
    }
    url.hash = '';
    return url.toString().slice(0, MAX_STRING_LENGTH);
  } catch {
    return (value.split(/[?#]/, 1)[0] || '').slice(0, MAX_STRING_LENGTH);
  }
}

function isForbiddenKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return FORBIDDEN_KEYS.has(normalized)
    || /^(auth|access|refresh|session|api)_?token$/.test(normalized)
    || /^card_/.test(normalized);
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/^\$/, '');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
